import { useEffect, useRef, useState } from "react";
import { Copy, Download, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

// Feuille de partage de la progression d'un set — même UX que la carte joueur, mais l'image
// est le PNG personnel « {pct}% de {set} » servi par l'endpoint premium.
export function ShareProgressSheet({ slug, setName, pct, open, onClose }) {
  const { t, lang } = useI18n();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const src = `${API}/collection/sets/${slug}/progress-card.png?lang=${lang}&layout=card`;
  const [drag, setDrag] = useState(0);
  const start = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => { if (open) setDrag(0); }, [open]);
  if (!open) return null;

  const pageUrl = window.location.href;
  const caption = `${pct}% — ${setName}`;

  const copy = async () => {
    await navigator.clipboard.writeText(pageUrl);
    toast.success(t("player.share_copied"));
  };
  const download = async () => {
    try {
      const blob = await (await fetch(src)).blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rookie-radar-${slug}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e?.message || "download failed");
    }
  };
  const share = async () => {
    try {
      const blob = await (await fetch(src)).blob();
      const file = new File([blob], `rookie-radar-${slug}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, url: pageUrl });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: caption, url: pageUrl });
        return;
      }
      await copy();
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e?.message || "share failed");
    }
  };

  const onTouchStart = (e) => { start.current = e.touches[0].clientY; };
  const onTouchMove = (e) => {
    if (start.current == null) return;
    setDrag(Math.max(0, e.touches[0].clientY - start.current));
  };
  const onTouchEnd = () => {
    if (drag > 120) onClose();
    setDrag(0);
    start.current = null;
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" data-testid="share-progress-sheet">
      <button aria-label={t("player.share_close")} onClick={onClose} data-testid="share-progress-backdrop"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div style={{ transform: `translateY(${drag}px)`, transition: drag ? "none" : "transform .25s ease-out" }}
        className="relative w-full max-w-md rounded-t-3xl border border-[#1F293D] bg-[#0B0E14] p-4 pb-24 shadow-[0_-10px_40px_rgba(0,0,0,.6)] sm:rounded-2xl sm:pb-4">
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
          className="mx-auto mb-3 h-1.5 w-12 cursor-grab rounded-full bg-[#1F293D] sm:hidden" data-testid="share-progress-grabber" />
        <div className="mb-3 flex items-center justify-between">
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">{t("sets.share_progress_title")}</p>
          <button onClick={onClose} data-testid="share-progress-close" aria-label={t("player.share_close")} className="text-slate-500 hover:text-[#00FF87]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <img src={src} alt={caption} data-testid="share-progress-preview"
          className="mx-auto max-h-[38vh] w-auto rounded-xl border border-[#1F293D] sm:max-h-[52vh]" />
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={share} data-testid="share-progress-share" className="flex flex-col items-center gap-1 rounded-lg border border-[#00FF87]/50 bg-[#00FF87]/10 py-2.5 font-mono2 text-[10px] uppercase tracking-wider text-[#00FF87] transition-colors hover:bg-[#00FF87]/20">
            <Share2 className="h-4 w-4" /> {t("player.share")}
          </button>
          <button onClick={download} data-testid="share-progress-download" className="flex flex-col items-center gap-1 rounded-lg border border-[#1F293D] py-2.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00F0FF]/60 hover:text-[#00F0FF]">
            <Download className="h-4 w-4" /> {t("player.share_download")}
          </button>
          <button onClick={copy} data-testid="share-progress-copy" className="flex flex-col items-center gap-1 rounded-lg border border-[#1F293D] py-2.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00F0FF]/60 hover:text-[#00F0FF]">
            <Copy className="h-4 w-4" /> {t("player.share_copy")}
          </button>
        </div>
      </div>
    </div>
  );
}
